import React, { useState } from 'react';
import { 
  Coins, PlusCircle, Loader2, ChevronLeft, 
  ChevronRight, History, Receipt, Clock, CheckCircle2, XCircle, Copy, 
  Check, Sparkles, Crown, Gem, CheckCheck, AlertCircle, RefreshCw
} from 'lucide-react';
import { useAuth } from '../AuthProvider';
import { Card } from './ui/Card';
import { useMyWalletRequests, useWalletTransactions, useConfirmTopUpPaymentMutation } from '../hooks/useQueries';
import { useQueryClient } from '@tanstack/react-query';

interface WalletSectionProps {
  onOpenTopUp: (initialTab?: 'XU' | 'VIP') => void;
}

interface TopUpRequestItem {
  id: string;
  _id?: string;
  paymentCode: string;
  packageName: string;
  packageType?: 'XU' | 'VIP' | 'SVIP';
  amountVnd: number;
  credits: number;
  status: 'WAITING_PAYMENT' | 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  processedAt?: string;
}

interface TransactionItem {
  id?: string;
  _id?: string;
  amount: number;
  balanceBefore?: number;
  balanceAfter?: number;
  type?: string;
  description?: string;
  createdAt?: string;
  date?: string;
  reason?: string;
  title?: string;
  status?: string;
}

const formatDate = (isoStr?: string): string => {
  if (!isoStr) return '---';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year} ${hours}:${mins}`;
  } catch {
    return isoStr || '---';
  }
};

export const WalletSection: React.FC<WalletSectionProps> = ({ onOpenTopUp }) => {
  const { userProfile, loading, refetchProfile, isMonetizationEnabled } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'topup' | 'all'>('topup');
  
  // Phân trang đơn nạp
  const [page, setPage] = useState<number>(1);
  const pageSize = 10;

  // Phân trang biến động số dư
  const [txPage, setTxPage] = useState<number>(1);
  const txPageSize = 10;

  // State cục bộ cho UI
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // React Query Hooks (Tự động cache & deduplicate, không giật màn hình khi chuyển trang)
  const { 
    data: requestsData, 
    isLoading: loadingRequests, 
    isFetching: isFetchingRequests,
    refetch: refetchRequests 
  } = useMyWalletRequests({ page, limit: pageSize });

  const { 
    data: transactionsData, 
    isLoading: loadingTx, 
    isFetching: isFetchingTx,
    refetch: refetchTx 
  } = useWalletTransactions({ page: txPage, limit: txPageSize });

  const confirmMutation = useConfirmTopUpPaymentMutation();

  const myRequests: TopUpRequestItem[] = requestsData?.data || [];
  const totalCount = requestsData?.pagination?.totalCount ?? 0;
  const totalPages = requestsData?.pagination?.totalPages ?? 1;

  const txList: TransactionItem[] = transactionsData?.data || [];
  const txTotalCount = transactionsData?.pagination?.totalCount ?? 0;
  const txTotalPages = transactionsData?.pagination?.totalPages ?? 1;

  const userCredits = typeof userProfile?.credits === 'number' ? userProfile.credits : 0;

  // Copy helper
  const handleCopy = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Xác nhận chuyển khoản cho đơn WAITING_PAYMENT
  const handleConfirmPayment = async (item: TopUpRequestItem) => {
    const reqId = item._id || item.id;
    if (!reqId) return;

    try {
      const res = await confirmMutation.mutateAsync(reqId);
      setToastMessage({
        type: 'success',
        message: res.message || 'Đã gửi xác nhận thanh toán! Đơn hàng đang được chờ duyệt và kích hoạt.'
      });
      refetchProfile();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('topup_success'));
      }
    } catch (err: any) {
      setToastMessage({
        type: 'error',
        message: err.message || 'Không thể gửi xác nhận thanh toán. Vui lòng thử lại.'
      });
    } finally {
      setTimeout(() => {
        setToastMessage(null);
      }, 5000);
    }
  };

  const handleRefreshData = () => {
    refetchProfile();
    if (activeTab === 'topup') {
      refetchRequests();
    } else {
      refetchTx();
    }
  };

  return (
    <div className="space-y-6">
      {/* Banner Xu Khả Dụng - Phối màu thương hiệu Nhà Giáo Số (Mix Xanh lá #236539 & Xanh dương #224783) */}
      <Card className="bg-gradient-to-r from-[#236539] via-[#1d5530] to-[#224783] text-white border-0 shadow-lg relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-white/10 blur-2xl transform rotate-12 pointer-events-none" />
        <div className="absolute -left-12 -bottom-12 w-40 h-40 bg-[#224783]/40 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10 p-2 sm:p-3">
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-emerald-200">
              <div className="w-6 h-6 rounded-full bg-emerald-400/20 flex items-center justify-center">
                <Coins className="w-4 h-4 text-emerald-300" />
              </div>
              <span className="text-xs font-black uppercase tracking-wider text-emerald-100">Ví Xu Khả Dụng</span>
            </div>

            {loading ? (
              <div className="h-10 w-48 bg-white/20 animate-pulse rounded-xl" />
            ) : (
              <div className="flex items-baseline space-x-2">
                <span className="text-4xl md:text-5xl font-black tracking-tight text-white drop-shadow-xs">
                  {userCredits.toLocaleString('vi-VN')}
                </span>
                <span className="text-xl font-bold text-emerald-200">Xu</span>
              </div>
            )}

            <p className="text-xs text-emerald-50/90 max-w-md leading-relaxed">
              Xu được dùng để sử dụng một số ứng dụng tại Nhà Giáo Số, tải các tài liệu có phí và mua sản phẩm số
            </p>
          </div>

          <div className="flex items-center flex-wrap gap-2.5 shrink-0">
            <button
              type="button"
              onClick={() => onOpenTopUp('XU')}
              className="px-4 sm:px-5 py-2.5 bg-white hover:bg-emerald-50 text-[#236539] font-black text-xs sm:text-sm rounded-xl shadow-md transition-all hover:shadow-lg active:scale-95 flex items-center space-x-1.5 cursor-pointer border border-white/40"
            >
              <PlusCircle className="w-4 h-4 text-[#236539]" />
              <span>{!isMonetizationEnabled ? 'Nhận Xu Miễn Phí' : 'Thêm Xu'}</span>
            </button>

            <button
              type="button"
              onClick={() => onOpenTopUp('VIP')}
              className="px-4 sm:px-5 py-2.5 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-slate-950 font-black text-xs sm:text-sm rounded-xl shadow-md transition-all hover:shadow-lg active:scale-95 flex items-center space-x-1.5 cursor-pointer border border-yellow-200/80 shadow-amber-900/20"
            >
              <Crown className="w-4 h-4 text-slate-950" />
              <span>{!isMonetizationEnabled ? 'Nhận VIP Miễn Phí' : 'Nâng Cấp VIP'}</span>
            </button>
          </div>
        </div>
      </Card>

      {/* Card Quản lý Lịch Sử */}
      <Card>
        <div className="space-y-4">
          {/* Sub-tab Navigation */}
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setActiveTab('topup')}
                className={`px-3 sm:px-4 py-2 rounded-xl text-[11px] sm:text-xs font-extrabold flex items-center space-x-1.5 sm:space-x-2 transition-all cursor-pointer ${
                  activeTab === 'topup'
                    ? 'bg-[#236539] text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Receipt className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline">Lịch sử giao dịch</span>
                <span className="sm:hidden">Lịch sử GD</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('all')}
                className={`px-3 sm:px-4 py-2 rounded-xl text-[11px] sm:text-xs font-extrabold flex items-center space-x-1.5 sm:space-x-2 transition-all cursor-pointer ${
                  activeTab === 'all'
                    ? 'bg-[#224783] text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <History className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline">Biến động số dư</span>
                <span className="sm:hidden">Biến động Xu</span>
              </button>
            </div>

            <button
              type="button"
              onClick={handleRefreshData}
              title="Cập nhật dữ liệu"
              className="px-2.5 sm:px-3 py-1.5 text-xs text-amber-800 hover:text-amber-900 font-bold bg-amber-50 hover:bg-amber-100 rounded-xl transition-colors border border-amber-200/80 cursor-pointer flex items-center space-x-1"
            >
              <RefreshCw className={`w-4 h-4 sm:w-3.5 sm:h-3.5 ${(isFetchingRequests || isFetchingTx) ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Cập nhật dữ liệu</span>
            </button>
          </div>

          {/* TAB 1: LỊCH SỬ ĐƠN HÀNG (PAGINATED TABLE) */}
          {activeTab === 'topup' && (
            <div className="space-y-4 animate-fade-in">
              {/* Toast Message Notification */}
              {toastMessage && (
                <div
                  className={`p-3.5 rounded-xl border text-xs font-semibold flex items-center justify-between gap-2 animate-fade-in shadow-2xs ${
                    toastMessage.type === 'success'
                      ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
                      : 'bg-rose-50 text-rose-900 border-rose-200'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    {toastMessage.type === 'success' ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                    )}
                    <span>{toastMessage.message}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setToastMessage(null)}
                    className="p-1 text-slate-400 hover:text-slate-600 rounded-md transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {loadingRequests ? (
                <div className="p-12 text-center text-slate-400 flex flex-col items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin mb-3 text-amber-500" />
                  <span className="text-xs font-semibold">Đang tải lịch sử đơn hàng...</span>
                </div>
              ) : myRequests.length === 0 ? (
                <div className="p-10 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-slate-200">
                  Bạn chưa tạo đơn hàng nào. Hãy bấm "Nạp thêm Xu ngay" để nâng cấp VIP hoặc nạp thêm Xu!
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto border border-slate-200 rounded-2xl bg-white shadow-2xs">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50/80 text-slate-500 font-bold border-b border-slate-200 uppercase tracking-wider text-[11px]">
                        <tr>
                          <th className="p-3.5 pl-4 whitespace-nowrap">Ngày tạo đơn</th>
                          <th className="p-3.5 whitespace-nowrap">Mã đơn (Payment Code)</th>
                          <th className="p-3.5">Tên gói</th>
                          <th className="p-3.5 whitespace-nowrap">Số tiền</th>
                          <th className="p-3.5 whitespace-nowrap">Quy đổi / Nhận</th>
                          <th className="p-3.5 text-center whitespace-nowrap">Trạng thái</th>
                          <th className="p-3.5 text-right pr-4 whitespace-nowrap">Thao tác</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {myRequests.map((item) => {
                          const isWaitingPayment = item.status === 'WAITING_PAYMENT';
                          const isPending = item.status === 'PENDING';
                          const isApproved = item.status === 'APPROVED';
                          const isRejected = item.status === 'REJECTED';
                          const code = item.paymentCode || item.id;
                          const isConfirming = confirmMutation.isPending;

                          const pkgType = item.packageType || (
                            item.packageName?.includes('SVIP') ? 'SVIP' :
                            item.packageName?.includes('VIP') ? 'VIP' : 'XU'
                          );

                          return (
                            <tr key={item.id || item._id} className="hover:bg-amber-50/20 transition-colors">
                              {/* 1. Ngày tạo đơn (createdAt) */}
                              <td className="p-3.5 pl-4 text-slate-600 whitespace-nowrap font-medium">
                                <div className="flex items-center space-x-1.5">
                                  <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                  <span>{formatDate(item.createdAt)}</span>
                                </div>
                              </td>

                              {/* 2. Mã đơn (paymentCode) */}
                              <td className="p-3.5 font-mono whitespace-nowrap">
                                <div className="flex items-center space-x-1.5">
                                  <span className="font-extrabold text-slate-900 bg-slate-100 hover:bg-slate-200/80 px-2 py-0.5 rounded-md border border-slate-200 transition-colors">
                                    {code}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleCopy(code)}
                                    className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors cursor-pointer"
                                    title="Sao chép mã đơn"
                                  >
                                    {copiedCode === code ? (
                                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                                    ) : (
                                      <Copy className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                </div>
                              </td>

                              {/* 3. Tên gói (packageName) */}
                              <td className="p-3.5 font-bold text-slate-800">
                                <div className="flex items-center space-x-1.5">
                                  {pkgType === 'SVIP' ? (
                                    <span className="px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 font-extrabold text-[10px] rounded-md inline-flex items-center space-x-1">
                                      <Gem className="w-2.5 h-2.5 text-rose-600" />
                                      <span>SVIP</span>
                                    </span>
                                  ) : pkgType === 'VIP' ? (
                                    <span className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 font-extrabold text-[10px] rounded-md inline-flex items-center space-x-1">
                                      <Crown className="w-2.5 h-2.5 text-purple-600" />
                                      <span>VIP</span>
                                    </span>
                                  ) : (
                                    <span className="px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 font-extrabold text-[10px] rounded-md inline-flex items-center space-x-1">
                                      <Coins className="w-2.5 h-2.5 text-amber-600" />
                                      <span>XU</span>
                                    </span>
                                  )}
                                  <span className="text-slate-900">{item.packageName || 'Gói Nạp Xu'}</span>
                                </div>
                              </td>

                              {/* 4. Số tiền (amountVnd) */}
                              <td className="p-3.5 whitespace-nowrap font-extrabold text-emerald-700 text-[13px]">
                                {(item.amountVnd ?? 0).toLocaleString('vi-VN')} VNĐ
                              </td>

                              {/* 5. Số xu nhận / Quy đổi */}
                              <td className="p-3.5 whitespace-nowrap font-extrabold text-amber-900">
                                <span className="px-2 py-0.5 bg-amber-50 rounded-md text-amber-900 border border-amber-200 font-extrabold">
                                  +{(item.credits ?? 0).toLocaleString('vi-VN')} Xu
                                </span>
                              </td>

                              {/* 6. Trạng thái (Status Badge) */}
                              <td className="p-3.5 text-center whitespace-nowrap">
                                {isWaitingPayment ? (
                                  <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-300/80 inline-flex items-center space-x-1 shadow-2xs">
                                    <Clock className="w-3 h-3 text-amber-600 shrink-0" />
                                    <span>Chưa xác nhận thanh toán</span>
                                  </span>
                                ) : isPending ? (
                                  <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center space-x-1">
                                    <Clock className="w-3 h-3 text-blue-600 shrink-0" />
                                    <span>Đang chờ duyệt</span>
                                  </span>
                                ) : isApproved ? (
                                  <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 inline-flex items-center space-x-1">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                                    <span>Thành công</span>
                                  </span>
                                ) : (
                                  <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-50 text-rose-800 border border-rose-200 inline-flex items-center space-x-1">
                                    <XCircle className="w-3 h-3 text-rose-600 shrink-0" />
                                    <span>Bị từ chối</span>
                                  </span>
                                )}
                              </td>

                              {/* 7. Thao tác (Nút Xác nhận ngay cho đơn WAITING_PAYMENT) */}
                              <td className="p-3.5 text-right pr-4 whitespace-nowrap">
                                {isWaitingPayment ? (
                                  <button
                                    type="button"
                                    disabled={isConfirming}
                                    onClick={() => handleConfirmPayment(item)}
                                    className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 active:scale-95 text-white font-extrabold text-xs rounded-xl shadow-xs hover:shadow transition-all inline-flex items-center space-x-1.5 cursor-pointer disabled:opacity-50"
                                    title="Xác nhận bạn đã chuyển khoản để ban quản trị duyệt đơn"
                                  >
                                    {isConfirming ? (
                                      <>
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        <span>Đang gửi...</span>
                                      </>
                                    ) : (
                                      <>
                                        <CheckCheck className="w-3.5 h-3.5" />
                                        <span>Xác nhận ngay</span>
                                      </>
                                    )}
                                  </button>
                                ) : isPending ? (
                                  <span className="text-[11px] font-medium text-slate-400 italic">
                                    Đang chờ Admin duyệt
                                  </span>
                                ) : isApproved ? (
                                  <span className="text-[11px] font-bold text-emerald-600 inline-flex items-center space-x-1">
                                    <Check className="w-3.5 h-3.5" />
                                    <span>Hoàn tất</span>
                                  </span>
                                ) : (
                                  <span className="text-[11px] font-medium text-rose-400">
                                    Đã hủy
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Thanh Phân Trang (Pagination Controls) */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs">
                    <div className="text-slate-500 font-medium">
                      Hiển thị trang <strong className="text-slate-900">{page}</strong> / <strong className="text-slate-900">{totalPages}</strong> (Tổng <strong>{totalCount}</strong> đơn hàng)
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        disabled={page <= 1 || isFetchingRequests}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1 cursor-pointer"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span>Trang trước</span>
                      </button>

                      <button
                        type="button"
                        disabled={page >= totalPages || isFetchingRequests}
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1 cursor-pointer"
                      >
                        <span>Trang sau</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* TAB 2: BIẾN ĐỘNG SỐ DƯ (TẤT CẢ GIAO DỊCH - CENTRALIZED LEDGER) */}
          {activeTab === 'all' && (
            <div className="space-y-4 animate-fade-in">
              {loadingTx ? (
                <div className="p-12 text-center text-slate-400 flex flex-col items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin mb-3 text-amber-500" />
                  <span className="text-xs font-semibold">Đang tải lịch sử biến động Xu...</span>
                </div>
              ) : txList.length === 0 ? (
                <div className="p-10 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-slate-200">
                  Chưa có lịch sử biến động số dư nào.
                </div>
              ) : (
                <>
                  {/* 1. Bảng Dữ Liệu Biến Động Số Dư (Table) */}
                  <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-2xs">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50/90 text-[11px] sm:text-xs font-bold text-slate-600 uppercase tracking-wider">
                          <th className="p-3.5 pl-4 whitespace-nowrap">Thời gian</th>
                          <th className="p-3.5">Nội dung</th>
                          <th className="p-3.5 text-right whitespace-nowrap">Biến động</th>
                          <th className="p-3.5 pr-4 text-right whitespace-nowrap">Số dư cuối</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-xs sm:text-sm">
                        {txList.map((tx, idx) => {
                          const amountVal = Number(tx.amount || 0);
                          const isPositive = amountVal > 0;
                          const isNegative = amountVal < 0;
                          const balanceAfterVal = typeof tx.balanceAfter === 'number' 
                            ? tx.balanceAfter 
                            : (typeof (tx as any).balance === 'number' ? (tx as any).balance : undefined);
                          const descriptionText = tx.description || tx.reason || tx.title || tx.type || 'Biến động số dư';

                          return (
                            <tr 
                              key={tx.id || tx._id || `tx-${idx}`} 
                              className="hover:bg-slate-50/80 transition-colors"
                            >
                              {/* 1. Thời gian: DD/MM/YYYY HH:mm */}
                              <td className="p-3.5 pl-4 whitespace-nowrap text-slate-500 font-medium text-xs">
                                {formatDate(tx.createdAt || tx.date)}
                              </td>

                              {/* 2. Nội dung: description */}
                              <td className="p-3.5 font-medium text-slate-900">
                                <span className="line-clamp-2 leading-relaxed">
                                  {descriptionText}
                                </span>
                              </td>

                              {/* 3. Biến động: amount */}
                              <td className="p-3.5 text-right whitespace-nowrap font-bold">
                                {isPositive ? (
                                  <span className="text-emerald-600">
                                    +{amountVal.toLocaleString('vi-VN')} Xu
                                  </span>
                                ) : isNegative ? (
                                  <span className="text-rose-600">
                                    {amountVal.toLocaleString('vi-VN')} Xu
                                  </span>
                                ) : (
                                  <span className="text-slate-500 font-medium">
                                    0 Xu
                                  </span>
                                )}
                              </td>

                              {/* 4. Số dư cuối: balanceAfter (Text in đậm, màu đen/xám) */}
                              <td className="p-3.5 pr-4 text-right whitespace-nowrap">
                                <span className="font-bold text-slate-900">
                                  {balanceAfterVal !== undefined ? `${balanceAfterVal.toLocaleString('vi-VN')} Xu` : '---'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* 2. Thanh Phân Trang Biến Động Số Dư (Server-side Pagination) */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs">
                    <div className="text-slate-500 font-medium">
                      Hiển thị trang <strong className="text-slate-900">{txPage}</strong> / <strong className="text-slate-900">{txTotalPages}</strong> (Tổng <strong>{txTotalCount}</strong> biến động)
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        disabled={txPage <= 1 || isFetchingTx}
                        onClick={() => setTxPage(p => Math.max(1, p - 1))}
                        className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1 cursor-pointer"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span>Trang trước</span>
                      </button>

                      <button
                        type="button"
                        disabled={txPage >= txTotalPages || isFetchingTx}
                        onClick={() => setTxPage(p => Math.min(txTotalPages, p + 1))}
                        className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1 cursor-pointer"
                      >
                        <span>Trang sau</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default WalletSection;
